import React from 'react';
import { Link } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { displayName, initialsFromMember } from './batchDetailUtils';

function metricChipColor(value) {
  if (value == null || Number.isNaN(value)) return 'default';
  if (value >= 85) return 'success';
  if (value >= 70) return 'info';
  if (value >= 50) return 'warning';
  return 'error';
}

function MetricBlock({ loading, error, value, isPercent }) {
  if (loading) {
    return (
      <Box sx={{ py: 0.5 }}>
        <CircularProgress size={18} />
      </Box>
    );
  }
  if (error) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }
  if (value == null) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }
  return (
    <Chip
      label={`${value}${isPercent ? '%' : ''}`}
      color={metricChipColor(value)}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 700, borderWidth: 1.5 }}
    />
  );
}

export default function BatchStudentRoster({ members, statsByStudentId, archived, onRemoveMember }) {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
  const count = (members || []).length;

  const confirmRemove = (m) => {
    const name = displayName(m);
    if (
      !window.confirm(
        `Remove ${name || m.email} from this batch?\n\nThey will keep existing test assignments and history.`
      )
    ) {
      return;
    }
    onRemoveMember(m.student);
  };

  if (count === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 5,
          textAlign: 'center',
          borderRadius: 2,
          border: '1px dashed',
          borderColor: 'divider',
          bgcolor: (t) => (t.palette.mode === 'light' ? 'grey.50' : 'action.hover'),
        }}
      >
        <InboxOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          No students in this batch yet
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
          Use enrollment above to add students. You can then assign tests or exam prep plans to everyone at once.
        </Typography>
      </Paper>
    );
  }

  const rowCells = (m) => {
    const st = statsByStudentId[m.student];
    const name = displayName(m);
    return (
      <>
        <TableCell sx={{ py: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ width: 40, height: 40, fontWeight: 700, bgcolor: 'primary.main' }}>{initialsFromMember(m)}</Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                {name}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {m.email}
              </Typography>
            </Box>
          </Stack>
        </TableCell>
        <TableCell align="center">
          <MetricBlock loading={st?.loading} error={st?.error} value={st?.avgScore} isPercent />
        </TableCell>
        <TableCell align="center">
          <Tooltip title="Among tests with a due date: share submitted on or before that date.">
            <span>
              <MetricBlock loading={st?.loading} error={st?.error} value={st?.timelyPct} isPercent />
            </span>
          </Tooltip>
        </TableCell>
        <TableCell align="right">
          <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center" flexWrap="wrap" useFlexGap>
            <Button
              component={Link}
              to={`/teacher/assignments?student=${m.student}`}
              variant="outlined"
              size="small"
              startIcon={<AssignmentIndOutlinedIcon />}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              Assignments
            </Button>
            <Tooltip title="Remove from batch">
              <span>
                <IconButton
                  size="small"
                  color="default"
                  disabled={archived}
                  onClick={() => confirmRemove(m)}
                  sx={{
                    opacity: 0.55,
                    transition: 'opacity 0.15s',
                    '&:hover': { opacity: 1, color: 'error.main' },
                    '&:focus-visible': { opacity: 1, outline: '2px solid', outlineOffset: 2, outlineColor: 'error.main' },
                  }}
                  aria-label={`Remove ${m.email} from batch`}
                >
                  <PersonRemoveOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </TableCell>
      </>
    );
  };

  if (isNarrow) {
    return (
      <Stack spacing={2}>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ px: 0.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Student list
          </Typography>
          <Chip label={`${count} enrolled`} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
        </Stack>
        {members.map((m) => {
          const st = statsByStudentId[m.student];
          const name = displayName(m);
          return (
            <Card
              key={m.id}
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: theme.shadows[3] },
              }}
            >
              <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                    <Avatar sx={{ width: 44, height: 44, fontWeight: 700 }}>{initialsFromMember(m)}</Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                        {name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {m.email}
                      </Typography>
                    </Box>
                  </Stack>
                  <Tooltip title="Remove from batch">
                    <span>
                      <IconButton size="small" disabled={archived} onClick={() => confirmRemove(m)} aria-label="Remove from batch">
                        <PersonRemoveOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                <Stack direction="row" spacing={2} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                      Avg score
                    </Typography>
                    <MetricBlock loading={st?.loading} error={st?.error} value={st?.avgScore} isPercent />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                      On-time
                    </Typography>
                    <MetricBlock loading={st?.loading} error={st?.error} value={st?.timelyPct} isPercent />
                  </Box>
                </Stack>
                <Button
                  component={Link}
                  to={`/teacher/assignments?student=${m.student}`}
                  variant="contained"
                  color="inherit"
                  fullWidth
                  sx={{
                    mt: 2,
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: 2,
                    bgcolor: 'action.selected',
                    color: 'primary.main',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  startIcon={<AssignmentIndOutlinedIcon />}
                >
                  Open assignments
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    );
  }

  return (
    <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Student list
        </Typography>
        <Chip label={`${count} enrolled`} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
      </Stack>
      <TableContainer>
        <Table size="medium" aria-label="Students in this batch">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, py: 1.75, bgcolor: 'action.hover', color: 'text.secondary', fontSize: 12, letterSpacing: 0.04, textTransform: 'uppercase' }}>
                Student
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, py: 1.75, bgcolor: 'action.hover', color: 'text.secondary', fontSize: 12, letterSpacing: 0.04, textTransform: 'uppercase', width: 120 }}>
                Avg score
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, py: 1.75, bgcolor: 'action.hover', color: 'text.secondary', fontSize: 12, letterSpacing: 0.04, textTransform: 'uppercase', width: 140 }}>
                On-time
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, py: 1.75, bgcolor: 'action.hover', color: 'text.secondary', fontSize: 12, letterSpacing: 0.04, textTransform: 'uppercase', width: 220 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members.map((m) => (
              <TableRow
                key={m.id}
                hover
                sx={{
                  '&:nth-of-type(even)': { bgcolor: 'action.hover' },
                  '&:last-child td': { borderBottom: 0 },
                }}
              >
                {rowCells(m)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ px: 2.5, py: 1.25, bgcolor: 'action.hover', borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary">
          On-time uses assignments that include a target date and a submitted attempt.
        </Typography>
      </Box>
    </Paper>
  );
}
